/**
 * Export options for the printed slate.
 *
 * The only number that really matters here is the printed size of each code.
 * A HERO reads a code reliably from about 40 mm across; below that you get a
 * page that looks fine on screen and refuses to scan on a rocking boat. So the
 * dialog computes that figure from the current options and says so out loud
 * before anyone commits a sheet of paper to it.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import type { CompiledSlot } from "../lib/shelf";
import {
  DEFAULT_SLATE_OPTIONS,
  MIN_QR_MM,
  measureQrMm,
  PAGE_SIZES,
  slateBlob,
  type PageSizeKey,
  type SlateOptions,
} from "../lib/slate";

interface SlateDialogProps {
  slots: readonly CompiledSlot[];
  open: boolean;
  onClose: () => void;
}

export function SlateDialog({ slots, open, onClose }: SlateDialogProps) {
  const [options, setOptions] = useState<SlateOptions>(DEFAULT_SLATE_OPTIONS);
  const [error, setError] = useState<string | null>(null);
  // The renderer is a lazily-loaded chunk, so generating is no longer
  // instantaneous the first time in a session. It is normally a cache hit and
  // over in a frame or two, which is exactly why the buttons need to be
  // disabled rather than showing a spinner nobody will ever see.
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const printable = useMemo(() => slots.filter((s) => s.ok && s.payload), [slots]);
  const skipped = slots.length - printable.length;
  const qrMm = measureQrMm(options.pageSize, options.showCommand, printable);
  const tooSmall = qrMm < MIN_QR_MM;

  async function exportPdf(action: "download" | "preview") {
    setError(null);
    setBusy(true);
    try {
      const { blob, filename } = await slateBlob(printable, options);
      const url = URL.createObjectURL(blob);
      if (action === "preview") {
        window.open(url, "_blank", "noopener");
        // Revoked late: the new tab needs the URL to survive the handoff.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        onClose();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "The PDF could not be generated.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="m-auto w-[min(94vw,520px)] rounded-[3px] border-2 border-graphite bg-paper p-0 text-graphite backdrop:bg-graphite/50"
    >
      <form method="dialog" className="contents">
        <div className="border-b border-slate-line px-4 py-3">
          <h2 className="text-[22px] font-bold uppercase leading-none">Print the slate</h2>
          <p className="mt-1 text-[13px] text-graphite-2">
            {printable.length} preset{printable.length === 1 ? "" : "s"} on one page
            {skipped > 0 && `, ${skipped} left out because ${skipped === 1 ? "it does" : "they do"} not compile`}
            .
          </p>
        </div>

        <div className="grid gap-3.5 px-4 py-3.5">
          <label className="grid gap-1">
            <span className="font-display text-[13px] font-semibold uppercase tracking-[0.16em] text-graphite-2">
              Heading
            </span>
            <input
              value={options.heading}
              maxLength={48}
              onChange={(e) => setOptions((o) => ({ ...o, heading: e.target.value }))}
              className="border border-slate-line bg-field px-2 py-1.5 font-body text-sm"
            />
          </label>

          <label className="grid gap-1">
            <span className="font-display text-[13px] font-semibold uppercase tracking-[0.16em] text-graphite-2">
              Note under it
            </span>
            <input
              value={options.subheading}
              maxLength={90}
              placeholder="Liveaboard, Red Sea, March — HERO13"
              onChange={(e) => setOptions((o) => ({ ...o, subheading: e.target.value }))}
              className="border border-slate-line bg-field px-2 py-1.5 font-body text-sm"
            />
          </label>

          <fieldset className="m-0 border-0 p-0">
            <legend className="mb-1 font-display text-[13px] font-semibold uppercase tracking-[0.16em] text-graphite-2">
              Paper
            </legend>
            <div className="flex gap-[5px]">
              {(Object.keys(PAGE_SIZES) as PageSizeKey[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={options.pageSize === key}
                  onClick={() => setOptions((o) => ({ ...o, pageSize: key }))}
                  className={[
                    "cursor-pointer rounded-[2px] border-[1.5px] border-graphite px-[11px] py-[5px] font-display text-[15px] font-semibold uppercase tracking-[0.05em]",
                    options.pageSize === key ? "bg-graphite text-paper" : "text-graphite",
                  ].join(" ")}
                >
                  {PAGE_SIZES[key].label}
                </button>
              ))}
            </div>
          </fieldset>

          <Toggle
            checked={options.showCommand}
            onChange={(v) => setOptions((o) => ({ ...o, showCommand: v }))}
            label="Print the command under each code"
            hint="Lets you check or retype a preset without the app. Costs a few millimetres of QR."
          />

          <Toggle
            checked={options.showCutLines}
            onChange={(v) => setOptions((o) => ({ ...o, showCutLines: v }))}
            label="Cut guides"
            hint="Dashed borders, for turning the page into six laminated tags."
          />

          <p
            className={[
              "m-0 border-l-[3px] px-2.5 py-1.5 text-[12.5px]",
              tooSmall ? "border-flag bg-flag/10" : "border-deep bg-deep/10",
            ].join(" ")}
          >
            Each code prints about <strong>{qrMm} mm</strong> across.{" "}
            {tooSmall
              ? `That is under the ${MIN_QR_MM} mm a HERO reads reliably. Turn the command off, or print fewer presets per page.`
              : "Comfortably scannable. Print at 100%, not “fit to page”."}
          </p>

          {error && (
            <p className="m-0 border-l-[3px] border-flag bg-flag/10 px-2.5 py-1.5 text-[12.5px]">
              {error}
            </p>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-[7px] border-t border-slate-line px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer rounded-[2px] border-[1.5px] border-graphite bg-transparent px-3.5 py-[7px] font-display text-[15px] font-semibold uppercase tracking-[0.06em] text-graphite"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void exportPdf("preview")}
            disabled={!printable.length || busy}
            className="cursor-pointer rounded-[2px] border-[1.5px] border-graphite bg-transparent px-3.5 py-[7px] font-display text-[15px] font-semibold uppercase tracking-[0.06em] text-graphite disabled:opacity-40"
          >
            Preview
          </button>
          <button
            type="button"
            onClick={() => void exportPdf("download")}
            disabled={!printable.length || busy}
            className="cursor-pointer rounded-[2px] border-none bg-deep px-3.5 py-[7px] font-display text-[15px] font-semibold uppercase tracking-[0.06em] text-paper disabled:opacity-40"
          >
            {busy ? "Preparing…" : "Save PDF"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  hint: string;
}) {
  return (
    <label className="grid cursor-pointer grid-cols-[18px_1fr] items-start gap-2.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-[18px] w-[18px] accent-[var(--color-deep)]"
      />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-[11.5px] leading-snug text-graphite-2">{hint}</span>
      </span>
    </label>
  );
}
